/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-empty-function */

import { Component, forwardRef, OnInit, OnDestroy, inject } from '@angular/core';
import { ControlValueAccessor, FormBuilder, FormGroup, NG_VALUE_ACCESSOR, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { DatepickerComponent } from '@premotec/pmt-datepicker';
import { TimepickerComponent } from 'ngx-bootstrap/timepicker';

@Component({
    selector: 'app-date-time-picker',
    templateUrl: './date-time-picker.component.html',
    styleUrl: './date-time-picker.component.scss',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DateTimePickerComponent),
            multi: true
        }
    ],
    imports: [FormsModule, ReactiveFormsModule, DatepickerComponent, TimepickerComponent]
})
export class DateTimePickerComponent implements ControlValueAccessor, OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly destroy$ = new Subject<void>();

  public form!: FormGroup;

  constructor() {
    this.form = this.fb.group({
      date: this.fb.control<Date | null>(null, Validators.required),
      time: this.fb.control<string | null>(null, Validators.required)
    });
  }

  protected onChange: (value: unknown) => void = () => {};
  protected onTouched: () => void = () => {};

  public ngOnInit(): void {
    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.onInputChange();
      });

    this.form.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.markAsTouched();
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public writeValue(value: Date | string | null): void {
    if (!value) {
      this.form.patchValue({ date: null, time: null });
      return;
    }

    let dateObj: Date;

    if (value instanceof Date) {
      dateObj = value;
    } else if (typeof value === 'string') {
      const isoRegex = /^(\d{4}-\d{2}-\d{2})[T\s]?(\d{2}:\d{2})?/;
      const isoMatch = isoRegex.exec(value);
      if (isoMatch) {
        const datePart = isoMatch[1];
        const timePart = isoMatch[2] || '00:00';
        dateObj = new Date(`${datePart}T${timePart}`);
      } else {
        dateObj = new Date(value);
      }
    } else {
      this.form.patchValue({ date: null, time: null });
      return;
    }

    if (isNaN(dateObj.getTime())) {
      this.form.patchValue({ date: null, time: null });
      return;
    }

    this.form.patchValue({
      date: new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate()),
      time: dateObj
    });
  }

  public registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    // optional
  }

  public markAsTouched(): void {
    if (this.form.get('date')?.touched && this.form.get('time')?.touched) {
      this.onTouched();
    }
  }

  public onInputChange(): void {
    const date = this.form.get('date')?.value as Date | null;
    const time = this.form.get('time')?.value as string;

    if (date && time) {
      const sourceDate = new Date(time);
      const targetDate = new Date(date);

      targetDate.setHours(sourceDate.getHours());
      targetDate.setMinutes(sourceDate.getMinutes());
      targetDate.setSeconds(0, 0);

      this.onChange(targetDate);
    } else {
      this.onChange(null);
    }
  }
}
